import React, { useRef, useState } from "react";
import { Box, CircularProgress, InputBase, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { NAVY } from "./utils";

const CampoEdicaoInline = ({
    valor,
    onSalvar,
    disabled = false,
    ariaLabel,
    tipo = "numero",
    permitirVazio = false
}) => {
    const [editando, setEditando] = useState(false);
    const [valorEdit, setValorEdit] = useState("");
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState("");
    // Evita salvar duas vezes: o Enter salva e o blur disparado ao fechar o input salvaria de novo.
    const estadoRef = useRef("ocioso");

    function iniciarEdicao() {
        if (disabled || salvando) return;
        estadoRef.current = "ocioso";
        setValorEdit(valor === null || valor === undefined ? "" : String(valor));
        setErro("");
        setEditando(true);
    }

    function cancelar() {
        estadoRef.current = "encerrado";
        setEditando(false);
        setErro("");
    }

    async function salvar() {
        if (estadoRef.current !== "ocioso") return;

        const texto = String(valorEdit).trim();

        if (texto === "" && !permitirVazio) {
            setErro("Campo obrigatório");
            return;
        }
        if (tipo === "numero" && Number.isNaN(Number(texto))) {
            setErro("Deve ser numérico");
            return;
        }

        const valorAtual = valor === null || valor === undefined ? "" : String(valor);
        const inalterado = tipo === "numero" && texto !== ""
            ? Number(texto) === Number(valorAtual || NaN)
            : texto === valorAtual;

        if (inalterado) {
            estadoRef.current = "encerrado";
            setEditando(false);
            setErro("");
            return;
        }

        estadoRef.current = "salvando";
        setSalvando(true);
        setErro("");
        try {
            await onSalvar(tipo === "numero" ? Number(texto) : texto);
            estadoRef.current = "encerrado";
            setEditando(false);
        } catch (e) {
            estadoRef.current = "ocioso";
            setErro(e?.response?.data?.mensagem || "Erro ao salvar. Tente novamente.");
        } finally {
            setSalvando(false);
        }
    }

    function handleKeyDown(event) {
        if (event.key === "Enter") {
            event.preventDefault();
            salvar();
        } else if (event.key === "Escape") {
            event.preventDefault();
            cancelar();
        }
    }

    function handleBlur() {
        salvar();
    }

    if (!editando) {
        const vazio = valor === null || valor === undefined || valor === "";

        return (
            <Box>
                <Typography
                    component="span"
                    role={disabled ? undefined : "button"}
                    tabIndex={disabled ? undefined : 0}
                    aria-label={ariaLabel}
                    onClick={iniciarEdicao}
                    onKeyDown={(e) => e.key === "Enter" && iniciarEdicao()}
                    variant="body2"
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "inherit",
                        cursor: disabled ? "default" : "pointer",
                        borderRadius: "6px",
                        px: 0.5,
                        mx: -0.5,
                        "&:hover": disabled ? {} : { color: NAVY, backgroundColor: "rgba(19, 35, 80, 0.06)" },
                        "&:hover .icone-editar": { opacity: 1 }
                    }}
                >
                    {vazio ? "—" : valor}
                    {!disabled && (
                        <EditOutlinedIcon
                            className="icone-editar"
                            sx={{ fontSize: "0.95rem", opacity: 0, transition: "opacity 0.15s ease" }}
                        />
                    )}
                </Typography>
                {erro && (
                    <Typography variant="caption" color="error" display="block">
                        {erro}
                    </Typography>
                )}
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <InputBase
                    autoFocus
                    type={tipo === "numero" ? "number" : "text"}
                    value={valorEdit}
                    disabled={salvando}
                    inputProps={{ "aria-label": ariaLabel }}
                    onChange={(e) => setValorEdit(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    sx={{
                        width: tipo === "numero" ? "6.5rem" : "12rem",
                        px: 1,
                        fontSize: "0.875rem",
                        border: `1px solid ${NAVY}`,
                        borderRadius: 1,
                        backgroundColor: "white"
                    }}
                />
                {salvando && <CircularProgress size={14} />}
            </Box>
            {erro && (
                <Typography variant="caption" color="error" display="block">
                    {erro}
                </Typography>
            )}
        </Box>
    );
};

export default React.memo(CampoEdicaoInline);
